"use client"

import { useEffect, useRef, useState } from "react"
import { LogOut, User as UserIcon } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/context/AuthContext"
import { cn } from "@/lib/utils"

interface UserAvatarMenuProps {
  /** Label shown above the avatar in the dropdown header. */
  scopeLabel?: string
  /** Click handler for the "Account" item. Required so the parent decides
   *  whether to navigate to the platform page or to an in-app account view. */
  onAccountClick: () => void
  /** Optional extra menu items rendered between Account and Logout. */
  extraItems?: React.ReactNode
  className?: string
}

function initialsFor(name: string | null | undefined): string {
  if (!name) return "?"
  const cleaned = name.trim()
  if (!cleaned) return "?"
  // For emails, use the local part.
  const base = cleaned.includes("@") ? cleaned.split("@")[0] : cleaned
  const parts = base.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return base.slice(0, 2).toUpperCase()
}

export function UserAvatarMenu({
  scopeLabel,
  onAccountClick,
  extraItems,
  className,
}: UserAvatarMenuProps) {
  const { username, appRole, platformRole, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    document.addEventListener("keydown", escHandler)
    return () => {
      document.removeEventListener("mousedown", handler)
      document.removeEventListener("keydown", escHandler)
    }
  }, [open])

  const initials = initialsFor(username)
  const roleLabel = platformRole === "admin" ? "Admin" : appRole || ""

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        className="size-9 rounded-full bg-background/50 backdrop-blur-sm hover:bg-background/80 p-0"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open account menu"
      >
        <Avatar className="size-9">
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-60 rounded-lg border bg-popover text-popover-foreground shadow-lg backdrop-blur z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b">
            {scopeLabel && (
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                {scopeLabel}
              </p>
            )}
            <p className="font-medium truncate">{username || "Guest"}</p>
            {roleLabel && (
              <p className="text-xs text-muted-foreground capitalize">{roleLabel}</p>
            )}
          </div>
          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onAccountClick()
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <UserIcon className="size-4" />
              Account
            </button>
            {extraItems}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                logout()
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-danger-subtle-foreground transition-colors hover:bg-accent"
            >
              <LogOut className="size-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
